import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import Footer from '../../components/Footer';
import { Cookie } from 'lucide-react';

export default function CookiesPage() {
  return (
    <div className="min-h-screen bg-[#F5F7FA]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_mercamove/artifacts/ch8knfdf_Move_Logo_wHT.png" 
                alt="Mercapoli Move"
                className="h-8"
              />
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost" className="text-slate-700 hover:text-[#00BFA6]">
                  Iniciar Sesión
                </Button>
              </Link>
              <Link to="/register">
                <Button className="btn-primary px-6 py-2 text-sm">
                  Registrarse
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="pt-24 pb-16 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-3xl shadow-lg p-8 md:p-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 bg-[#FFD166]/10 rounded-lg flex items-center justify-center">
                <span className="text-xl">🍪</span>
              </div>
              <h1 className="text-3xl font-bold text-slate-900 font-['Outfit']">
                Política de Cookies
              </h1>
            </div>
            <p className="text-slate-500 mb-8">Última actualización: Enero 2025</p>

            <div className="prose prose-slate max-w-none">
              <h2 className="text-xl font-bold text-slate-800 mt-8 mb-4">1. ¿Qué son las Cookies?</h2>
              <p className="text-slate-600 mb-4">
                Las cookies son pequeños archivos de texto que se almacenan en su dispositivo cuando 
                visita nuestra plataforma. Nos ayudan a proporcionar una mejor experiencia de usuario 
                y a entender cómo interactúa con nuestros servicios.
              </p>

              <h2 className="text-xl font-bold text-slate-800 mt-8 mb-4">2. Tipos de Cookies que Utilizamos</h2>
              
              <h3 className="text-lg font-semibold text-slate-700 mt-6 mb-3">2.1 Cookies Esenciales</h3>
              <p className="text-slate-600 mb-4">
                Son necesarias para el funcionamiento básico de la plataforma. Incluyen:
              </p>
              <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
                <li>Cookies de sesión de usuario</li>
                <li>Cookies de autenticación</li>
                <li>Cookies de seguridad</li>
                <li>Cookies de preferencias de idioma</li>
              </ul>

              <h3 className="text-lg font-semibold text-slate-700 mt-6 mb-3">2.2 Cookies de Rendimiento</h3>
              <p className="text-slate-600 mb-4">
                Nos ayudan a entender cómo los usuarios interactúan con nuestra plataforma:
              </p>
              <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
                <li>Páginas visitadas</li>
                <li>Tiempo de carga</li>
                <li>Errores encontrados</li>
                <li>Patrones de navegación</li>
              </ul>

              <h3 className="text-lg font-semibold text-slate-700 mt-6 mb-3">2.3 Cookies Funcionales</h3>
              <p className="text-slate-600 mb-4">
                Permiten recordar sus preferencias y personalizar su experiencia:
              </p>
              <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
                <li>Preferencias de notificaciones</li>
                <li>Ubicaciones guardadas</li>
                <li>Configuraciones de la cuenta</li>
              </ul>

              <h3 className="text-lg font-semibold text-slate-700 mt-6 mb-3">2.4 Cookies de Marketing</h3>
              <p className="text-slate-600 mb-4">
                Utilizadas para mostrar anuncios relevantes:
              </p>
              <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
                <li>Seguimiento de conversiones</li>
                <li>Remarketing</li>
                <li>Análisis de campañas</li>
              </ul>

              <h2 className="text-xl font-bold text-slate-800 mt-8 mb-4">3. Cookies de Terceros</h2>
              <p className="text-slate-600 mb-4">
                Utilizamos servicios de terceros que pueden establecer sus propias cookies:
              </p>
              <div className="bg-slate-50 p-4 rounded-xl mb-4">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-2 text-slate-700">Proveedor</th>
                      <th className="text-left py-2 text-slate-700">Propósito</th>
                    </tr>
                  </thead>
                  <tbody className="text-slate-600">
                    <tr className="border-b border-slate-100">
                      <td className="py-2">Google Analytics</td>
                      <td className="py-2">Análisis de uso</td>
                    </tr>
                    <tr className="border-b border-slate-100">
                      <td className="py-2">Stripe</td>
                      <td className="py-2">Procesamiento de pagos</td>
                    </tr>
                    <tr className="border-b border-slate-100">
                      <td className="py-2">Mapbox/Leaflet</td>
                      <td className="py-2">Servicios de mapas</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h2 className="text-xl font-bold text-slate-800 mt-8 mb-4">4. Control de Cookies</h2>
              <p className="text-slate-600 mb-4">
                Puede controlar las cookies de varias maneras:
              </p>
              <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
                <li><strong>Configuración del navegador:</strong> La mayoría de los navegadores permiten bloquear o eliminar cookies</li>
                <li><strong>Configuración de la plataforma:</strong> Puede ajustar sus preferencias de cookies en su perfil</li>
                <li><strong>Opt-out de terceros:</strong> Visite los sitios de los proveedores para gestionar sus preferencias</li>
              </ul>

              <div className="bg-[#FFD166]/10 p-4 rounded-xl mb-4">
                <p className="text-slate-700 text-sm">
                  <strong>Nota:</strong> Deshabilitar ciertas cookies puede afectar la funcionalidad de la plataforma.
                </p>
              </div>

              <h2 className="text-xl font-bold text-slate-800 mt-8 mb-4">5. Tecnologías Similares</h2>
              <p className="text-slate-600 mb-4">
                Además de cookies, utilizamos tecnologías similares como:
              </p>
              <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
                <li><strong>Local Storage:</strong> Para almacenar datos de sesión</li>
                <li><strong>Pixels de seguimiento:</strong> Para análisis de emails</li>
                <li><strong>Identificadores de dispositivo:</strong> Para seguridad y prevención de fraude</li>
              </ul>

              <h2 className="text-xl font-bold text-slate-800 mt-8 mb-4">6. Actualizaciones</h2>
              <p className="text-slate-600 mb-4">
                Podemos actualizar esta Política de Cookies periódicamente. Los cambios serán 
                efectivos inmediatamente después de su publicación en la plataforma.
              </p>

              <h2 className="text-xl font-bold text-slate-800 mt-8 mb-4">7. Contacto</h2>
              <p className="text-slate-600">
                Si tiene preguntas sobre nuestra política de cookies:<br />
                <strong>Email:</strong> cookies@mercapolimove.com
              </p>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
